import React, { useMemo, useState, useEffect } from "react";
import { connect } from "react-redux";
import { Actions as EMPLOYEEACTIONS } from "../../../store/actions/employeeActions";
import FyorinEmployeeStatus from "../../integrations/fyorin/FyorinEmployeeStatus";
import PaymentIntegrationEmployeeStatus from "../../integrations/paymentIntegrations/PaymentIntegrationEmployeeStatus";
import { NotificationManager } from "react-notifications";
import UkModulusChecking from "uk-modulus-checking";
import { merge, setWith } from "lodash";
import { uuid } from "uuidv4";
import cleanDeep from "clean-deep";
import ReactSelect from "react-select";

const ibantools = require("ibantools");

const payByOptions = [
	{
		label: "Bank",
		value: false,
	},
	{
		label: "Manual Transfer / Cheque",
		value: true,
	},
];

const Bank = ({
	bank,
	saveBankDetails,
	savingBank,
	disabled,
	engagementDate,
	jurisdiction,
	forNewEmployee,
	nextPage,
	prevPage,
	forReviewAgreement,
	notification,
}) => {
	const [data, setData] = useState({});
	const updateField = (field, value) => {
		if (!field) return;
		if (field.includes("[")) field = setWith({}, field, value, Object);
		const newData = merge(data, { ...(typeof field == "object" ? field : { [field]: value }) });

		setData({ ...newData });
	};

	const bankID = useMemo(() => {
		return bank?.id ? String(bank?.id) : undefined;
	}, [bank?.id]);

	useEffect(() => {
		const hasBank = bank && Object.keys(cleanDeep(bank))?.length > 0;
		if (hasBank) {
			setData({
				payByCheque:
					typeof bank?.payByCheque === "undefined"
						? false
						: ["true", "yes", true].includes(bank?.payByCheque),
				bank: bank?.bank || "",
				accountHolder: bank?.accountHolder || "",
				sortCode: bank?.sortCode || "",
				iban: bank?.iban || "",
				bic: bank?.bic || "",
				accountNumber: bank?.accountNumber || "",
			});
		} else {
			setData({ payByCheque: false });
		}
	}, [bankID]);

	const onSubmit = (e) => {
		e.preventDefault();
		e.stopPropagation();
		data.start = engagementDate;

		if (!data.payByCheque) {
			switch (jurisdiction) {
				case "MT":
					const ibanCountry = data.iban?.substr(0, 2);
					const bicCountry = data.bic
						?.substr(4, 2)
						.replace(/IM|GG|JE/, "GB")
						.replace(/GF|GP|MQ|RE|PF|TF|YT|NC|BL|MF|PM|WF/, "FR");

					if (!data.iban || !ibantools.isValidIBAN(data.iban)) {
						NotificationManager.error("IBAN is Invalid", "IBAN Validity");
						return;
					}

					if (!data.bic || !ibantools.isValidBIC(data.bic)) {
						NotificationManager.error("BIC is Invalid", "BIC Validity");
						return;
					}

					if (ibanCountry != bicCountry) {
						NotificationManager.error(`IBAN and BIC Country Codes Don't Match`, "Country Codes");
						return;
					}
					break;

				case "UK":
					if (
						!new UkModulusChecking({ accountNumber: data.accountNumber, sortCode: data.sortCode }).isValid()
					) {
						NotificationManager.error(
							`Account Number or Sort Code is Invalid`,
							"Account Number & Sort Codes",
						);
						return;
					}
					break;
			}
		}

		saveBankDetails(data, forReviewAgreement, notification, nextPage);
		// console.log('Submited Data', data);
	};

	return (
		<form
			onSubmit={onSubmit}
			className="px-8"
		>
			<div className="col-xs-12 form-section">
				<div
					className="col-xs-4 section-header"
					style={{ margin: 0, paddingBottom: 20 }}
				>
					<h1 className="block-title">Bank Details</h1>
				</div>
				<div className="col-xs-12">
					<div className="col-xs-12 pl-0">
						<div className="col-xs-4 pl-0 pr-0">
							<div className="form-material form-container">
								<label
									htmlFor="payByCheque"
									style={{ width: "100%" }}
								>
									Pay by
								</label>
								<ReactSelect
									value={payByOptions.find((item) => item.value == data.payByCheque)}
									options={payByOptions}
									onChange={(selected) => updateField("payByCheque", selected?.value)}
								/>
							</div>
						</div>
						<div className="col-xs-1 pl-0">
							<FyorinEmployeeStatus />
							<PaymentIntegrationEmployeeStatus />
						</div>
						<div className="clearfix" />

						<div className="col-xs-12 pl-0">
							<div className="col-xs-6 pl-0">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="bank"
										name="bank"
										disabled={disabled}
										value={data?.bank}
										onChange={(e) => updateField(e.target.name, e.target.value)}
									/>
									<label htmlFor="bank">Bank</label>
								</div>
							</div>
							<div className="col-xs-6 pl-0">
								<div className="form-material form-container">
									<input
										className="form-control form-input"
										id="accountHolder"
										name="accountHolder"
										disabled={disabled}
										value={data?.accountHolder}
										onChange={(e) => updateField(e.target.name, e.target.value)}
									/>
									<label htmlFor="accountHolder">Account Holder Name</label>
								</div>
							</div>
						</div>

						<div className="col-xs-12 pl-0">
							{jurisdiction == "MT" && (
								<>
									<div className="col-xs-6 pl-0">
										<div className="form-material form-container">
											<input
												className="form-control form-input"
												id="bic"
												name="bic"
												disabled={disabled}
												value={data?.bic}
												onChange={(e) => updateField(e.target.name, e.target.value)}
											/>
											<label htmlFor="bic">BIC/Swift Code</label>
										</div>
									</div>

									<div className="col-xs-6 pl-0">
										<div className="form-material form-container">
											<input
												className="form-control form-input"
												id="iban"
												name="iban"
												disabled={disabled}
												value={data?.iban}
												onChange={(e) => {
													const value = e.target.value.replace(/\s/g, "");
													updateField(e.target.name, value);
												}}
											/>
											<label htmlFor="iban">IBAN</label>
										</div>
									</div>
								</>
							)}

							{jurisdiction == "UK" && (
								<>
									<div className="col-xs-6 pl-0">
										<div className="form-material form-container">
											<input
												className="form-control form-input"
												id="sortCode"
												name="sortCode"
												disabled={disabled}
												value={data?.sortCode}
												onChange={(e) => updateField(e.target.name, e.target.value)}
											/>
											<label htmlFor="sortCode">Sort Code</label>
										</div>
									</div>

									<div className="col-xs-6 pl-0">
										<div className="form-material form-container">
											<input
												className="form-control form-input"
												id="accountNumber"
												name="accountNumber"
												disabled={disabled}
												value={data?.accountNumber}
												onChange={(e) => updateField(e.target.name, e.target.value)}
											/>
											<label htmlFor="accountNumber">Account Number</label>
										</div>
									</div>
								</>
							)}
						</div>
					</div>
				</div>
			</div>
			<div className="clearfix" />
			{!forNewEmployee && !disabled && (
				<button
					className="btn btn-default btn-success pull-right"
					type="submit"
				>
					{savingBank ? "Saving Bank Details..." : "Save Bank Details"}
				</button>
			)}
			{forNewEmployee && (
				<div>
					{prevPage && (
						<button
							className="btn btn-default"
							onClick={prevPage}
						>
							Back
						</button>
					)}
					<button
						className="btn btn-success pull-right"
						type="submit"
					>
						Save &amp; Continue
					</button>
					<button
						className="btn btn-default pull-right"
						onClick={nextPage}
						style={{ marginRight: 20 }}
					>
						Skip
					</button>
				</div>
			)}
			<div className="clearfix" />
		</form>
	);
};

const mapStateToProps = (state, ownProps) => {
	const { bankDetails, savingBank } = state.employee;

	const { forReview } = ownProps;

	return {
		jurisdiction: state.company?.settings?.jurisdiction,
		bank: forReview ? forReview : bankDetails?.[bankDetails?.length - 1],
		savingBank,
		disabled: state?.permissions?.my?.permissions?.people == "Read",
		engagementDate: state.employee?.employeeDetails?.engagementDate,
	};
};

const mapDispatchToProps = (dispatch) => ({
	saveBankDetails: (input, agreement, notification, onSuccess) =>
		dispatch(EMPLOYEEACTIONS.saveBankDetails(input, agreement, notification, onSuccess)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Bank);
